import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import type { WorkflowStepEvent } from './types';

// Overall WorkflowProcess status event (shared across all workflows).
const OVERALL_STATUS_EVENT = 'crm-workflow-process-status-changed';

// One step in the persisted process fetched from `queries.processStatus.href`
// (GET /api/jobs/processes/:id -> `{ data: as_json_with_steps }`). Fields are
// snake_case: this body is rendered raw via `render(json:)`, NOT Jbuilder-
// camelized (mirrors the Aries MDB restore shape).
interface PersistedProcessStep {
  type?: string;
  event_name: string;
  status: string;
  failure_reason?: string | null;
}

interface PersistedProcessResponse {
  data?: {
    overall_status?: string;
    steps?: PersistedProcessStep[];
  };
}

const STATUS_DISPLAY_MAP: Record<string, string> = {
  pending: 'Pending',
  processing: 'In Progress',
  processed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
  unchanged: 'Unchanged',
  unnecessary: 'Unnecessary',
};

// Monotonic lifecycle rank for merging a persisted snapshot against live state:
// pending < processing < terminal. A step ends in exactly one terminal state,
// so they share the top rank. Hydration applies a persisted status only when it
// out-ranks the current one — so it can advance a live `processing` row to a
// persisted terminal (the missed-terminal-event case) but never regress.
const STATUS_RANK: Record<string, number> = {
  pending: 0,
  processing: 1,
  processed: 2,
  failed: 2,
  skipped: 2,
  blocked: 2,
  unchanged: 2,
  unnecessary: 2,
};

const statusRank = (status: string | undefined): number => {
  if (!status) return 0;
  return STATUS_RANK[status] ?? 0;
};

interface ProgressState {
  statusItems: StatusItem[];
  overallStatus: string;
}

type ProgressAction =
  | { type: 'reset' }
  | { type: 'initialize'; steps: WorkflowStepEvent[] }
  | {
      type: 'updateStep';
      name: string;
      status: string;
      details?: string | null;
    }
  | { type: 'setOverallStatus'; overallStatus: string }
  | {
      type: 'hydrate';
      steps: PersistedProcessStep[];
      overallStatus: string;
    };

const reducer = (
  state: ProgressState,
  action: ProgressAction
): ProgressState => {
  switch (action.type) {
    case 'reset':
      return { statusItems: [], overallStatus: 'pending' };
    case 'initialize':
      return {
        overallStatus: 'pending',
        statusItems: action.steps.map((step) => ({
          label: step.label ?? '',
          status: 'pending',
          value: 'Pending',
          name: step.name,
        })),
      };
    case 'updateStep':
      return {
        ...state,
        statusItems: state.statusItems.map((item) => {
          if (item.name !== action.name) return item;
          // Don't regress a completed step on an out-of-order message.
          if (item.status === 'processed' && action.status !== 'processed')
            return item;
          return {
            ...item,
            status: action.status,
            value: STATUS_DISPLAY_MAP[action.status] || action.status,
            details: action.details ?? item.details,
          };
        }),
      };
    case 'setOverallStatus':
      return { ...state, overallStatus: action.overallStatus };
    case 'hydrate': {
      // Fill in the persisted server state fetched after subscribing, so the
      // initial `processing` notification (emitted before the 202 rendered, and
      // never replayed by Pusher) and any events lost to a fast completion or a
      // brief disconnect are recovered. This is a MERGE by monotonic rank, not a
      // reset: the persisted status is applied only when it OUT-RANKS the current
      // one, so it advances a still-`pending` row and also lifts a live
      // `processing` row to a persisted terminal (recovering a lost terminal
      // Pusher event) — but never regresses an already-advanced row. `overallStatus`
      // follows the same rule.
      const persistedByName = new Map(
        action.steps.map((step) => [step.event_name, step])
      );
      return {
        overallStatus:
          statusRank(action.overallStatus) > statusRank(state.overallStatus)
            ? action.overallStatus
            : state.overallStatus,
        statusItems: state.statusItems.map((item) => {
          const persisted = persistedByName.get(item.name);
          if (
            !persisted ||
            statusRank(persisted.status) <= statusRank(item.status)
          )
            return item;
          return {
            ...item,
            status: persisted.status,
            value: STATUS_DISPLAY_MAP[persisted.status] || persisted.status,
            details: persisted.failure_reason ?? item.details,
          };
        }),
      };
    }
  }
};

interface UseIcWorkspaceProgressParams {
  /** Workflow Pusher channel (crm_workflow_process_{id}), or null for none. */
  channel: string | null;
  /** The kickoff response's notification events (1 overall + N steps). */
  events: WorkflowStepEvent[] | null;
  /**
   * The kickoff response's `queries.processStatus.href`
   * (GET /api/jobs/processes/:id). Fetched once after subscribing to hydrate the
   * persisted per-step + overall status, recovering any event that fired before
   * the subscription was live (the initial `processing`, a fast completion, a
   * brief disconnect). Null skips hydration (live-only).
   */
  processStatusHref: string | null;
}

interface UseIcWorkspaceProgressReturn {
  statusItems: StatusItem[];
  overallStatus: string;
  isBuilding: boolean;
  isComplete: boolean;
  isFailed: boolean;
}

/**
 * Subscribes to an IC WorkspaceCreationProcess channel and tracks per-step
 * progress for the shared WorkflowStatusPopover. Checklist rows + labels are
 * server-driven (the kickoff `events`); each per-step Pusher event advances a
 * row.
 *
 * To avoid missing events that fired before the subscription was live — the
 * process's initial `processing` notification is emitted before the 202 renders
 * and Pusher does not replay, and a fast completion / brief disconnect can drop
 * terminal or step events — the hook subscribes first and THEN fetches the
 * persisted process once from `processStatusHref`, merging its statuses in
 * without regressing any row a live event already advanced.
 *
 * @param {UseIcWorkspaceProgressParams} params - Channel + kickoff events + process-status href.
 * @return {UseIcWorkspaceProgressReturn} Progress state for the popover.
 */
export const useIcWorkspaceProgress = ({
  channel,
  events,
  processStatusHref,
}: UseIcWorkspaceProgressParams): UseIcWorkspaceProgressReturn => {
  const [state, dispatch] = useReducer(reducer, {
    statusItems: [],
    overallStatus: 'pending',
  });

  // The checklist rows are the events that carry a step_type (the overall
  // descriptor has none) — the same filter useTractWorkflow uses.
  const steps = useMemo<WorkflowStepEvent[]>(
    () => (events ?? []).filter((event) => event.step_type),
    [events]
  );

  const stepNamesRef = useRef<string[]>([]);
  stepNamesRef.current = steps.map((step) => step.name);

  useEffect(() => {
    if (steps.length > 0) {
      dispatch({ type: 'initialize', steps });
    } else {
      dispatch({ type: 'reset' });
    }
  }, [steps]);

  const createStepHandler = useCallback(
    (eventName: string) =>
      (data: { status?: string; details?: string | null }) => {
        if (!data?.status) return;
        dispatch({
          type: 'updateStep',
          name: eventName,
          status: data.status,
          details: data.details,
        });
      },
    []
  );

  useEffect(() => {
    if (!channel) return undefined;

    const subscription = subscribe(channel);

    subscription.bind(
      OVERALL_STATUS_EVENT,
      (data: { overall_status?: string }) => {
        if (data?.overall_status) {
          dispatch({
            type: 'setOverallStatus',
            overallStatus: data.overall_status,
          });
        }
      }
    );

    const stepNames = stepNamesRef.current;
    stepNames.forEach((name) =>
      subscription.bind(name, createStepHandler(name))
    );

    return () => {
      subscription.unbind(OVERALL_STATUS_EVENT);
      stepNames.forEach((name) => subscription.unbind(name));
      subscription.unsubscribe();
    };
  }, [channel, createStepHandler]);

  // Hydrate from the persisted process AFTER the subscribe effect above has run
  // (effects fire top-to-bottom on mount, so the subscription is already live by
  // the time this fetch is issued — no event can slip through the gap). The
  // fetched snapshot fills any row still `pending`; live events, which keep
  // flowing on the open channel, always win. Runs once per href.
  useEffect(() => {
    if (!processStatusHref) return undefined;

    const abortController = new AbortController();
    let cancelled = false;

    request
      .get<PersistedProcessResponse>(processStatusHref, abortController)
      .then((response) => {
        if (cancelled || 'error' in response) return;
        const data = response.data;
        if (!data) return;
        dispatch({
          type: 'hydrate',
          steps: (data.steps ?? []).filter((step) => step.type),
          overallStatus: data.overall_status ?? 'pending',
        });
      });

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [processStatusHref]);

  return useMemo(
    () => ({
      statusItems: state.statusItems,
      overallStatus: state.overallStatus,
      isBuilding:
        state.overallStatus === 'processing' ||
        state.overallStatus === 'pending',
      isComplete: state.overallStatus === 'processed',
      isFailed: state.overallStatus === 'failed',
    }),
    [state]
  );
};
